import { useCallback, useEffect, useMemo } from 'react';
import {
  CodeBlock,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  InlineStack,
  Page,
  PageBody,
  Pagination,
  Panel,
  PanelSection,
  Select,
  SkeletonText,
  TextLabel,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useParams, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { isEqual } from 'lodash-es';
import { DocumentTitle } from '@/components/page/document-title';
import { CollectionDetailHeader, CollectionImportingState, useCollectionWorkspaceLimits } from '@/features/collections';
import { useUserRoleForCurrentWorkspace } from '@/features/roles';
import { useRegisterBreadcrumbs, type BreadcrumbItem } from '@/services/breadcrumbs';
import { collectionKeys, type Collection } from '@/services/db/collection/entity';
import { getCollectionCache, setCollectionCache } from '@/services/db/collection/cache';
import { useUpdateCollection } from '@/services/db/collection/mutations';
import { useCollection, useCollectionItems, type CollectionItem } from '@/services/db/collection/queries';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { lzQueryDecode, queryEncode } from '@/utils/condition-query-encoding';
import { isRecord } from '@/utils/objects';
import { CollectionFilterBuilder } from './components/collection-filter-builder';
import {
  isCollectionAttributeFilter,
  isCommittableCollectionAttributeFilter,
  isEmptyFilters,
} from './components/collection-filter-builder/dryad-utils';
import { CollectionItemsTable, type CollectionItemsTableSorting } from './components/collection-items-table';

const PER_PAGE = 50;

// services rejects the update outside 1–5 (ui_api/collections/update.go,
// validateDisplayedAttributes).
const MAX_DISPLAYED_ATTRIBUTES = 5;

const VIEW_AS_OPTIONS = [
  { label: 'Table', value: 'table' },
  { label: 'Raw data', value: 'raw' },
];

function getVisibleColumns(schema: string[], displayedAttributes: string[]): string[] {
  const uniqueSchema = [...new Set(schema)];
  const schemaAttributes = new Set(uniqueSchema);
  const visibleDisplayedAttributes = [...new Set(displayedAttributes)].filter((attribute) =>
    schemaAttributes.has(attribute),
  );

  return visibleDisplayedAttributes.length > 0 ? visibleDisplayedAttributes : uniqueSchema.slice(0, 5);
}

function normalizeViewAs(viewAs: string): 'raw' | 'table' {
  return viewAs === 'raw' ? 'raw' : 'table';
}

function normalizePage(page: number): number {
  return page < 1 ? 1 : page;
}

function decodedFiltersFromQueryParam(filters: string | null): unknown {
  if (filters == null) {
    return null;
  }

  try {
    return lzQueryDecode(filters);
  } catch {
    return null;
  }
}

function apiFiltersFromQueryParam(filters: string | null): string | null {
  const decodedFilters = decodedFiltersFromQueryParam(filters);
  return decodedFilters == null || isEmptyFilters(decodedFilters) ? null : filters;
}

function builderFiltersFromQueryParam(filters: string | null): string | null {
  const decodedFilters = decodedFiltersFromQueryParam(filters);
  const normalizedFilters = normalizeRenderableFilters(decodedFilters);

  return normalizedFilters == null || isEmptyFilters(normalizedFilters) ? null : queryEncode(normalizedFilters);
}

function normalizeRenderableFilters(filters: unknown): Record<string, unknown>[] | null {
  if (Array.isArray(filters)) {
    const collectionAttributeFilters = filters.filter(isCollectionAttributeFilter);
    return collectionAttributeFilters.length > 0 ? collectionAttributeFilters : null;
  }
  if (isRecord(filters) && Array.isArray(filters.and)) {
    return normalizeRenderableFilters(filters.and);
  }
  if (isCollectionAttributeFilter(filters)) {
    return [filters];
  }
  return null;
}

function committableCollectionAttributeFilters(conditions: unknown[]): unknown[] {
  return conditions.filter(isCommittableCollectionAttributeFilter);
}

// Only what services can decode survives an apply. Decode wants a flat array of
// type-tagged conditions, so a group envelope cannot ride along as an element:
// keeping one would fail the whole request and take the working filters with it.
function isPreservableCondition(filter: unknown): boolean {
  return isRecord(filter) && typeof filter.type === 'string';
}

function hiddenFiltersFromDecodedFilters(filters: unknown): unknown[] {
  if (Array.isArray(filters)) {
    return filters.filter((filter) => isPreservableCondition(filter) && !isCollectionAttributeFilter(filter));
  }
  if (isRecord(filters) && Array.isArray(filters.and)) {
    return filters.and.filter((filter) => isPreservableCondition(filter) && !isCollectionAttributeFilter(filter));
  }
  if (!isPreservableCondition(filters) || isCollectionAttributeFilter(filters)) {
    return [];
  }
  return [filters];
}

// Each renderable filter is replaced where it stood, so a hidden condition that
// sat between two collection filters is still between them after an apply.
// Surplus updated filters are new rows and go on the end.
function mergeAtOriginalPositions(originalFilters: unknown[], updatedFilters: unknown[]): unknown[] {
  let updatedIndex = 0;
  const merged: unknown[] = [];

  for (const filter of originalFilters) {
    if (!isCollectionAttributeFilter(filter)) {
      merged.push(filter);
    } else if (updatedIndex < updatedFilters.length) {
      merged.push(updatedFilters[updatedIndex]);
      updatedIndex += 1;
    }
  }

  return [...merged, ...updatedFilters.slice(updatedIndex)];
}

function mergeUpdatedFiltersWithHiddenFilters(currentFilters: string | null, updatedFilters: unknown[]): unknown {
  const decodedFilters = decodedFiltersFromQueryParam(currentFilters);
  const hiddenFilters = hiddenFiltersFromDecodedFilters(decodedFilters);

  if (hiddenFilters.length === 0) {
    return updatedFilters;
  }
  if (isRecord(decodedFilters) && Array.isArray(decodedFilters.and)) {
    // Unwrapped: the envelope is read tolerantly but never written back, since
    // Decode requires the top level to be an array.
    return mergeAtOriginalPositions(decodedFilters.and, updatedFilters);
  }
  if (Array.isArray(decodedFilters)) {
    return mergeAtOriginalPositions(decodedFilters, updatedFilters);
  }
  return [...hiddenFilters, ...updatedFilters];
}

function useCollectionDataQueryState() {
  const [viewAs, setViewAs] = useQueryState('viewAs', parseAsString.withDefault('table'));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [filters, setFilters] = useQueryState('filters', parseAsString);
  const [sort, setSort] = useQueryState('sort', parseAsString);
  const [asc, setAsc] = useQueryState('asc', parseAsBoolean.withDefault(false));

  return {
    asc,
    filters,
    page: normalizePage(page),
    setAsc,
    setFilters,
    setPage,
    setSort,
    setViewAs,
    sort,
    viewAs: normalizeViewAs(viewAs),
  };
}

type CollectionDataQueryState = ReturnType<typeof useCollectionDataQueryState>;

// An undecodable filters param drives nothing: the builder renders no rows and
// the request omits conditions. Left in the URL it strands the reader with a
// filter they can neither see nor remove, so drop it.
function useClearUndecodableFilters(filters: string | null, setFilters: CollectionDataQueryState['setFilters']) {
  const isUndecodable = filters != null && decodedFiltersFromQueryParam(filters) == null;

  useEffect(() => {
    if (isUndecodable) {
      void setFilters(null);
    }
  }, [isUndecodable, setFilters]);
}

type CollectionDataBreadcrumbsProps = {
  collectionId: string;
  collectionName: string;
  workspaceId: number;
};

function CollectionDataBreadcrumbs({ collectionId, collectionName, workspaceId }: CollectionDataBreadcrumbsProps) {
  const router = useRouter();
  const breadcrumbs = useMemo<BreadcrumbItem[]>(
    () => [
      {
        id: 'collections',
        text: 'Collections',
        href: router.buildLocation({ to: '/workspaces/$workspaceId/journeys/collections', params: { workspaceId } })
          .href,
      },
      {
        id: `collection-${collectionId}`,
        text: collectionName,
        href: router.buildLocation({
          to: '/workspaces/$workspaceId/journeys/collections/$collectionId',
          params: { workspaceId, collectionId },
        }).href,
        isTruncated: true,
      },
    ],
    [collectionId, collectionName, router, workspaceId],
  );
  useRegisterBreadcrumbs(breadcrumbs);
  return null;
}

type CollectionDataToolbarProps = {
  page: number;
  total: number;
  viewAs: 'raw' | 'table';
  onPageChange: (nextPage: number) => void;
  onViewAsChange: (value: string | null) => void;
};

function CollectionDataToolbar({ page, total, viewAs, onPageChange, onViewAsChange }: CollectionDataToolbarProps) {
  return (
    <InlineStack alignBlock="center" alignInline="space-between" mb="150">
      <InlineStack alignBlock="center" gap="100">
        <TextLabel id="view-as-label" htmlFor="view-as-select">
          View as
        </TextLabel>
        <Select
          ariaLabelledby="view-as-label"
          id="view-as-select"
          options={VIEW_AS_OPTIONS}
          value={viewAs}
          onChange={onViewAsChange}
          size="sm"
        />
      </InlineStack>
      <Pagination totalItemCount={total} pageSize={PER_PAGE} page={page} onPageChange={onPageChange} />
    </InlineStack>
  );
}

type EmptyCollectionItemsPanelProps = {
  hasFilters: boolean;
};

// Title matches the UI repo's single empty state; the filter hint is held back when no
// filters are set, so a genuinely empty collection isn't told to change filters it never
// applied.
function EmptyCollectionItemsPanel({ hasFilters }: EmptyCollectionItemsPanelProps) {
  return (
    <Panel my="150">
      <PanelSection>
        <EmptyState>
          <EmptyStateTitle icon="database">No collection items</EmptyStateTitle>
          {hasFilters ? <EmptyStateDescription>Try changing your filters.</EmptyStateDescription> : null}
        </EmptyState>
      </PanelSection>
    </Panel>
  );
}

type RawCollectionItemsPanelProps = {
  isLoading: boolean;
  items: unknown[];
};

function RawCollectionItemsPanel({ isLoading, items }: RawCollectionItemsPanelProps) {
  return (
    <Panel my="150">
      <PanelSection>
        {/* Only with nothing to show: rendering `[]` reads as an empty collection.
            Unlike the table, an in-flight refetch keeps the previous JSON on
            screen rather than replacing readable output with a skeleton. */}
        {isLoading && items.length === 0 ? (
          <SkeletonText text="product-p" lines={6} />
        ) : (
          <CodeBlock language="json" code={JSON.stringify(items, null, 2)} className="fs-mask" />
        )}
      </PanelSection>
    </Panel>
  );
}

type CollectionDataContentProps = {
  canEdit: boolean;
  collection: Collection;
  hasFilters: boolean;
  isLoading: boolean;
  items: CollectionItem[];
  sorting?: CollectionItemsTableSorting;
  total: number;
  viewAs: 'raw' | 'table';
  visibleColumns: string[];
  onSortingChange: (sorting: CollectionItemsTableSorting) => void;
  onVisibleColumnsChange: (attributes: string[]) => void;
};

function CollectionDataContent({
  canEdit,
  collection,
  hasFilters,
  isLoading,
  items,
  sorting,
  total,
  viewAs,
  visibleColumns,
  onSortingChange,
  onVisibleColumnsChange,
}: CollectionDataContentProps) {
  if (collection.importing) {
    return <CollectionImportingState />;
  }

  // Not while a fetch is in flight: the first one has no data yet, so this would
  // claim the collection is empty before anything has been asked for.
  if (!isLoading && items.length === 0 && total === 0) {
    return <EmptyCollectionItemsPanel hasFilters={hasFilters} />;
  }

  if (viewAs === 'raw') {
    return <RawCollectionItemsPanel isLoading={isLoading} items={items} />;
  }

  return (
    <CollectionItemsTable
      collection={collection}
      isLoading={isLoading}
      items={items}
      sorting={sorting}
      visibleColumns={visibleColumns}
      canEditDisplayedColumns={canEdit}
      maxDisplayedColumns={MAX_DISPLAYED_ATTRIBUTES}
      onSortingChange={onSortingChange}
      onVisibleColumnsChange={onVisibleColumnsChange}
    />
  );
}

function useCanEditDisplayedAttributes() {
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();

  return hasPermission('journeys/collections/edit') === true && userRole?.redacted !== true;
}

function useIsRedactedUser() {
  const userRole = useUserRoleForCurrentWorkspace();

  return userRole?.redacted === true;
}

type CollectionDataPageHandlersParams = {
  collection: Collection;
  queryState: CollectionDataQueryState;
  workspaceId: number;
};

// The menu is fully controlled off the cached collection, so without an optimistic write
// a toggle would not move until the PATCH returned, and a reorder made meanwhile would
// recompute from pre-toggle visibility and undo it. Scoping keeps two quick edits in order.
function useDisplayedAttributesUpdate(collection: Collection, workspaceId: number) {
  const qc = useQueryClient();
  const snackbars = useSnackbars();
  const { mutate } = useUpdateCollection({
    workspaceId,
    mutationConfig: {
      scope: { id: `collection-displayed-attributes-${collection.id}` },
      onMutate: ({ id, update }) => {
        if (update.update_type !== 'displayed_attributes') return;

        const cached = getCollectionCache(qc, workspaceId, id);
        if (cached == null) return;

        setCollectionCache(qc, workspaceId, {
          ...cached,
          displayed_attributes: update.displayed_attributes ?? [],
        });
      },
      // Refetched rather than rolled back: a scoped queue can hold several edits, so the
      // state before *this* one is not necessarily the state to restore.
      onError: (_error, { id }) => {
        void qc.invalidateQueries({ queryKey: collectionKeys.detail(workspaceId, id) });
        snackbars.create({ variant: 'error', message: "Displayed attributes couldn't be saved. Try again." });
      },
    },
  });

  // The columns menu emits on every open and on reorders that land where they
  // started, so an unchanged list is dropped rather than sent as a no-op PATCH.
  return useCallback(
    (attributes: string[]) => {
      // Not dead code despite the menu's own cap: a collection already stored over the
      // limit still reaches here on reorder, and services would reject that list.
      if (attributes.length > MAX_DISPLAYED_ATTRIBUTES) {
        snackbars.create({
          variant: 'error',
          message: `Displayed attributes couldn't be saved. Only ${MAX_DISPLAYED_ATTRIBUTES} can be displayed at once.`,
        });
        return;
      }

      if (isEqual(attributes, getVisibleColumns(collection.schema, collection.displayed_attributes))) return;

      mutate({
        id: collection.id,
        update: { update_type: 'displayed_attributes', displayed_attributes: attributes },
      });
    },
    [collection.displayed_attributes, collection.id, collection.schema, mutate],
  );
}

function useCollectionDataPageHandlers({ collection, queryState, workspaceId }: CollectionDataPageHandlersParams) {
  const router = useRouter();
  const handleVisibleColumnsChange = useDisplayedAttributesUpdate(collection, workspaceId);
  const { filters, setAsc, setFilters, setPage, setSort, setViewAs } = queryState;

  const handleSortingChange = useCallback(
    (next: CollectionItemsTableSorting) => {
      void setSort(next.id);
      void setAsc(!next.desc);
    },
    [setAsc, setSort],
  );
  const handleViewAsChange = (value: string | null) => {
    void setViewAs(value === 'raw' ? 'raw' : 'table');
  };
  const handlePageChange = (nextPage: number) => {
    void setPage(nextPage);
  };
  const updateFilters = useCallback(
    (conditions: unknown) => {
      const filterString =
        Array.isArray(conditions) || (conditions != null && typeof conditions === 'object')
          ? queryEncode(conditions as Record<string, unknown> | Record<string, unknown>[])
          : null;
      const nextFilters = filterString == null || isEmptyFilters(conditions) ? null : filterString;

      // Re-applying a filter without editing it leaves the result set alone, so
      // it should leave the reader's page alone too. Compared decoded, not by
      // encoded string: the builder re-serializes keys in its own order, so an
      // untouched filter round-trips to a different string.
      const conditionsChanged = !isEqual(
        decodedFiltersFromQueryParam(filters),
        nextFilters == null ? null : conditions,
      );

      if (conditionsChanged) {
        void setPage(1);
      }
      void setFilters(nextFilters);
    },
    [filters, setFilters, setPage],
  );
  const applyDryadFilters = useCallback(
    (conditions: unknown) => {
      if (!Array.isArray(conditions)) return;

      updateFilters(mergeUpdatedFiltersWithHiddenFilters(filters, committableCollectionAttributeFilters(conditions)));
    },
    [filters, updateFilters],
  );
  const handleCollectionDeleted = useCallback(() => {
    void router.navigate({ to: '/workspaces/$workspaceId/journeys/collections', params: { workspaceId } });
  }, [router, workspaceId]);

  return {
    applyDryadFilters,
    handleCollectionDeleted,
    handlePageChange,
    handleSortingChange,
    handleViewAsChange,
    handleVisibleColumnsChange,
  };
}

// The filters param feeds two different readers: the request wants it encoded
// as it arrived, the builder wants only the conditions it can render.
function useCollectionFilterParams({ filters, setFilters }: CollectionDataQueryState) {
  useClearUndecodableFilters(filters, setFilters);
  const apiFilters = useMemo(() => apiFiltersFromQueryParam(filters), [filters]);
  const builderFilters = useMemo(() => builderFiltersFromQueryParam(filters), [filters]);

  return { apiFilters, builderFilters };
}

type CollectionItemsForPageInput = Omit<Parameters<typeof useCollectionItems>[0], 'limit' | 'queryConfig'>;

// `data` is undefined only on the very first fetch — keepPreviousData covers
// every later key change — and the table draws its loading state over both.
function useCollectionItemsForPage(input: CollectionItemsForPageInput) {
  const itemsQuery = useCollectionItems({ ...input, limit: PER_PAGE });

  return {
    isItemsLoading: itemsQuery.isFetching,
    items: itemsQuery.data?.collection_items ?? [],
    total: itemsQuery.data?.total ?? 0,
  };
}

function useCollectionDataPageModel() {
  const { collectionId } = useParams({ strict: false });
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const canEditDisplayedAttributes = useCanEditDisplayedAttributes();
  const isRedactedUser = useIsRedactedUser();
  const queryState = useCollectionDataQueryState();
  const { asc, page, sort, viewAs } = queryState;
  const { apiFilters, builderFilters } = useCollectionFilterParams(queryState);
  const { data: collection } = useCollection({ id: collectionId ?? '' });
  const limits = useCollectionWorkspaceLimits(workspaceId);
  const { isItemsLoading, items, total } = useCollectionItemsForPage({
    workspaceId,
    collectionId: Number(collection.id),
    page,
    conditions: apiFilters,
    orderBy: sort,
    asc,
  });
  const visibleColumns = useMemo(
    () => getVisibleColumns(collection.schema, collection.displayed_attributes),
    [collection.displayed_attributes, collection.schema],
  );
  const sorting = useMemo<CollectionItemsTableSorting | undefined>(
    () => (sort == null ? undefined : { id: sort, desc: !asc }),
    [asc, sort],
  );
  const handlers = useCollectionDataPageHandlers({ collection, queryState, workspaceId });

  return {
    apiFilters,
    builderFilters,
    canEditDisplayedAttributes,
    collection,
    isRedactedUser,
    applyDryadFilters: handlers.applyDryadFilters,
    handleCollectionDeleted: handlers.handleCollectionDeleted,
    handlePageChange: handlers.handlePageChange,
    handleSortingChange: handlers.handleSortingChange,
    handleViewAsChange: handlers.handleViewAsChange,
    handleVisibleColumnsChange: handlers.handleVisibleColumnsChange,
    isItemsLoading,
    items,
    page,
    sorting,
    total,
    viewAs,
    visibleColumns,
    workspaceId,
    maxFileSizeBytes: limits.maxFileSizeBytes,
  };
}

export default function DataPage() {
  const pageModel = useCollectionDataPageModel();

  return (
    <>
      <DocumentTitle title={`${pageModel.collection.name} data`} />
      <CollectionDataBreadcrumbs
        collectionId={pageModel.collection.id}
        collectionName={pageModel.collection.name}
        workspaceId={pageModel.workspaceId}
      />
      <Page flexGrow="1">
        <CollectionDetailHeader
          collection={pageModel.collection}
          maxFileSizeBytes={pageModel.maxFileSizeBytes}
          workspaceId={pageModel.workspaceId}
          onDeleted={pageModel.handleCollectionDeleted}
        />
        <PageBody>
          <CollectionFilterBuilder
            key={pageModel.collection.id}
            conditions={pageModel.builderFilters}
            disabled={pageModel.isRedactedUser}
            schema={pageModel.collection.schema}
            onChange={pageModel.applyDryadFilters}
          />

          <CollectionDataToolbar
            page={pageModel.page}
            total={pageModel.total}
            viewAs={pageModel.viewAs}
            onPageChange={pageModel.handlePageChange}
            onViewAsChange={pageModel.handleViewAsChange}
          />

          <CollectionDataContent
            canEdit={pageModel.canEditDisplayedAttributes}
            collection={pageModel.collection}
            hasFilters={pageModel.apiFilters != null}
            isLoading={pageModel.isItemsLoading}
            items={pageModel.items}
            sorting={pageModel.sorting}
            total={pageModel.total}
            viewAs={pageModel.viewAs}
            visibleColumns={pageModel.visibleColumns}
            onSortingChange={pageModel.handleSortingChange}
            onVisibleColumnsChange={pageModel.handleVisibleColumnsChange}
          />

          <InlineStack alignInline="flex-end">
            <Pagination
              totalItemCount={pageModel.total}
              pageSize={PER_PAGE}
              page={pageModel.page}
              onPageChange={pageModel.handlePageChange}
            />
          </InlineStack>
        </PageBody>
      </Page>
    </>
  );
}
